import type { Visualize3dObject } from '../models/visualize3d-object';
import type { Object3D } from 'three';
import type { Visualize3dComponent } from '../v3d-components/visualize3d-component';

export function instanceOfOnObject3dInit(object: OnObject3dInit | Record<string, unknown>): object is OnObject3dInit {
  return 'initialize' in object;
}

export interface OnObject3dInit<T = Visualize3dComponent> {
  /**
   * Invoked after the threeJs object has been created and attached to it's parent
   * @param object3d ThreeJs object to initialize
   * @param v3dObject Object3d
   */
  initialize(object3d: Object3D, v3dObject: Visualize3dObject, component: T): void;
}

export function instanceOfOnObject3dClick(object: OnObject3dClick | Record<string, unknown>): object is OnObject3dClick {
  return 'onClick' in object;
}

export interface OnObject3dClick<T = Visualize3dComponent> {
  /**
   * Invoked on pointer click on the THREE.Object3D which corresponds to Object3D
   * @param object3d ThreeJs object or null if click didn't occur on object
   * @param v3dObject Object3d or null if click didn't occur on object
   */
  onClick(object3d: Object3D, v3dObject: Visualize3dObject | null, component: T): void;
}

export function instanceOfOnObject3dOnPointerUp(object: OnObject3dPointerUp | Record<string, unknown>): object is OnObject3dPointerUp {
  return 'onPointerUp' in object;
}

export interface OnObject3dPointerUp<T = Visualize3dComponent> {
  /**
   * Invoked on pointer up on the THREE.Object3D which corresponds to Object3D
   * @param object3d ThreeJs object
   * @param v3dObject Object3d
   */
  onPointerUp(object3d: Object3D, v3dObject: Visualize3dObject, component: T): void;
}

export function instanceOfOnObject3dOnPointerDown(
  object: OnObject3dPointerDown | Record<string, unknown>,
): object is OnObject3dPointerDown {
  return 'onPointerDown' in object;
}

export interface OnObject3dPointerDown<T = Visualize3dComponent> {
  /**
   * Invoked on pointer down on the THREE.Object3D which corresponds to Object3D
   * @param object3d ThreeJs object
   * @param v3dObject Object3d
   */
  onPointerDown(object3d: Object3D, v3dObject: Visualize3dObject, component: T): void;
}

export function instanceOfOnObject3dOnPointerEnter(
  object: OnObject3dPointerEnter | Record<string, unknown>,
): object is OnObject3dPointerEnter {
  return 'onPointerEnter' in object;
}

export interface OnObject3dPointerEnter<T = Visualize3dComponent> {
  /**
   * Invoked on pointer enter on the THREE.Object3D which corresponds to Object3D
   * @param object3d ThreeJs object
   * @param v3dObject Object3d
   */
  onPointerEnter(object3d: Object3D, v3dObject: Visualize3dObject, component: T): void;
}

export function instanceOfOnObject3dOnPointerExit(
  object: OnObject3dPointerExit | Record<string, unknown>,
): object is OnObject3dPointerExit {
  return 'onPointerExit' in object;
}

export interface OnObject3dPointerExit<T = Visualize3dComponent> {
  /**
   * Invoked on pointer exit on the THREE.Object3D which corresponds to Object3D
   * @param object3d ThreeJs object
   * @param v3dObject Object3d
   */
  onPointerExit(object3d: Object3D, v3dObject: Visualize3dObject, component: T): void;
}

export function instanceOfOnObject3dSelect(object: OnObject3DSelect | Record<string, unknown>): object is OnObject3DSelect {
  return 'onSelect' in object;
}

export interface OnObject3DSelect<T = Visualize3dComponent> {
  /**
   * Invoked when object becomes the selected object
   * @param object3d ThreeJs object
   * @param v3dObject Object3d
   */
  onSelect(object3d: Object3D, v3dObject: Visualize3dObject, component: T): void;
}

export function instanceOfOnObject3dDeselect(object: OnObject3dDeselect | Record<string, unknown>): object is OnObject3dDeselect {
  return 'onDeselect' in object;
}

export interface OnObject3dDeselect<T = Visualize3dComponent> {
  /**
   * Invoked when object becomes deselected
   * @param object3d ThreeJs object
   * @param v3dObject Object3d
   */
  onDeselect(object3d: Object3D, v3dObject: Visualize3dObject, component: T): void;
}
